import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import WorldMap from './charts/map/WorldMap'
import {
  calculateColorScale,
  clone,
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  getBadges,
  formatValues,
  isValidDataForColorScale,
  getRecentYearRange,
  getSuppressedCountries,
  formatUnitMult,
  getUnitMeasureItems,
  isNumeric,
} from '../common/utils/helper'
import { getSingleYearChartSubtitle } from './charts/common/helper'
import {
  MissingDataMapTypes,
  ScaleTypes,
  disaggregations,
  mapTypes,
} from '../common/utils/enum'
import { CATEGORICAL_UNITMEASURE } from '../common/utils/constants'

const WorldMapWrapper = ({
  selectedCountries,
  unitMeasureList,
}) => {
  const [features, setFeatures] = useState([])
  const countryCentroids = useSelector((state) => state.common.countryCentroids)
  const {
    featureProps: { site },
  } = useSelector((state) => state.common)
  const {
    filteredIndicatorData,
    filteredAggregateData,
    selectedSingleYear,
    selectedFilters,
    disaggregationData,
    selectedComparison,
    indicatorMetadata,
    disaggregationCountries,
    recentYearSelection,
    selectedUnitMeasure,
    unitMeasureName,
    boundaryType,
    indicatorSpecificRegions,
    countryBoundaries,
    regionBoundaries,
    continentBoundaries,
    selectedAverages,
    countryHierarchy,
    countryListForIndicator,
  } = useSelector((state) => state.indicatorProfile)

  const boundaries =
    boundaryType === mapTypes.Region
      ? regionBoundaries
      : boundaryType === mapTypes.Continent
        ? continentBoundaries
        : countryBoundaries

  const unitMeasureItems = getUnitMeasureItems(disaggregationData)
  const shortLabel =
    unitMeasureItems?.find((item) => item.code === selectedUnitMeasure)
      ?.short_label || ''

  useEffect(() => {
      if (boundaries && Object.keys(boundaries).length > 0) {
      let initialFeatures = JSON.parse(JSON.stringify(boundaries?.features))

      const dataToProcess =
        boundaryType === mapTypes.Region || boundaryType === mapTypes.Continent
          ? filteredAggregateData
          : filteredIndicatorData

      if (dataToProcess) {
        let indicatorDataPerYear = clone(dataToProcess)

        // filter countries by selected income groups
        if (
          boundaryType === mapTypes.IncomeGroup &&
          selectedAverages?.length > 0 &&
          countryHierarchy?.INCOME
        ) {
          const countriesInSelectedIncomeGroups = new Set()
          selectedAverages.forEach((incomeGroupId) => {
            const incomeGroup = countryHierarchy.INCOME.find(
              (income) => income.id === incomeGroupId
            )
            if (incomeGroup?.countries) {
              incomeGroup.countries.forEach((country) => {
                countriesInSelectedIncomeGroups.add(country.id)
              })
            }
          })
          indicatorDataPerYear = indicatorDataPerYear.filter((data) =>
            countriesInSelectedIncomeGroups.has(data[disaggregations.RefArea])
          )
        }

        indicatorDataPerYear.forEach((data) => {
          const year = data?.data?.xAxis?.[0]
          const value = data?.data?.yAxis?.[0]

          data.value = isValidDataForColorScale(value)
            ? selectedUnitMeasure === CATEGORICAL_UNITMEASURE
              ? value
              : formatValues(value)
            : null
          data.year = year

          let feature
          if (
            boundaryType === mapTypes.Country ||
            boundaryType === mapTypes.IncomeGroup
          ) {
            feature = initialFeatures.find(
              (f) => data[disaggregations.RefArea] === f.properties.ISO_A3
            )

            // suppression logic -- start
            const suppressedCountries =
              indicatorMetadata?.admin_metadata?.[site]?.visualization
                ?.suppression
            // const suppressedCountries = 'AUT'
            let isSuppressedCountry = getSuppressedCountries(
              suppressedCountries
            ).includes(data[disaggregations.RefArea])
            // suppression logic -- end

            if (feature && !isSuppressedCountry) {
              feature.properties = { ...feature.properties, ...data }
            }
          } else if (boundaryType === mapTypes.Region) {
            const allFeatures = initialFeatures.filter((f) => {
              const regionCodes = f.properties.WB_REGION?.split(',') || []
              return regionCodes.includes(data[disaggregations.RefArea])
            })

            allFeatures?.forEach((feature) => {
              feature.properties = { ...feature.properties, ...data }
            })
          } else if (boundaryType === mapTypes.Continent) {
            feature = initialFeatures.find((f) => {
              const continentName = f.properties.CONTINENT
              if (!continentName) return false

              const continentInCountryHierarchy =
                countryHierarchy?.CONTINENT?.find(
                  (item) => item.name === continentName
                )

              return (
                continentInCountryHierarchy &&
                continentInCountryHierarchy.id === data[disaggregations.RefArea]
              )
            })

            if (feature) {
              feature.properties = { ...feature.properties, ...data }
            }
          }
        })
      }
      setFeatures(initialFeatures)
    }
  }, [
    boundaries,
    filteredIndicatorData,
    filteredAggregateData,
    selectedSingleYear,
    recentYearSelection,
    boundaryType,
    selectedAverages,
    countryHierarchy,
    indicatorMetadata,
  ])
  // Color scheme for sequential, diverging, categorical --start
  let scaleType =
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.scale_type ||
    ScaleTypes.Sequential

  let missingDataColor = ''
  if (
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data
      ?.length &&
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data[0]
      ?.map
  ) {
    const missingDataType =
      indicatorMetadata?.admin_metadata?.[site]?.visualization?.missing_data[0]
        .map
    if (missingDataType === MissingDataMapTypes.SpecifyColor) {
      missingDataColor =
        indicatorMetadata?.admin_metadata?.[site]?.visualization
          ?.missing_data[0]?.no_data_color
    }
  }

  let values = []

  const mapData =
    boundaryType === mapTypes.IncomeGroup || boundaryType === mapTypes.Country
      ? filteredIndicatorData
      : filteredAggregateData

  // calculating legend scale on filtered data
  if (
    boundaryType === mapTypes.Country ||
    boundaryType === mapTypes.IncomeGroup
  ) {
    //condition to filter only country data(not regions, income groups etc)
    if (countryListForIndicator?.length > 0) {
      if (
        scaleType === ScaleTypes.Categorical ||
        selectedUnitMeasure === CATEGORICAL_UNITMEASURE
      ) {
        filteredIndicatorData.forEach((data) => {
          if (
            countryListForIndicator
              .map((item) => item.id)
              .includes(data[disaggregations.RefArea])
          ) {
            const value = data?.data?.yAxis?.[0]

            if (value && value !== 'null' && value !== 'NaN') {
              values.push(value)
            }
          }
        })
      } else {
        filteredIndicatorData.forEach((data) => {
          if (
            countryListForIndicator
              .map((item) => item.id)
              .includes(data[disaggregations.RefArea])
          ) {
            const value = data?.data?.yAxis?.[0]

            if (isValidDataForColorScale(value)) {
              const formattedValue = formatValues(value)
              values.push(formattedValue)
            }
          }
        })
      }
    }
  } else {
    if (
      scaleType === ScaleTypes.Categorical ||
      selectedUnitMeasure === CATEGORICAL_UNITMEASURE
    ) {
      filteredAggregateData?.forEach((data) => {
        const value = data?.data?.yAxis?.[0]

        if (value && value !== 'null' && value !== 'NaN') {
          values.push(value)
        }
      })
    } else {
      filteredAggregateData?.forEach((data) => {
        const value = data?.data?.yAxis?.[0]

        if (isValidDataForColorScale(value)) {
          const formattedValue = formatValues(value)
          values.push(formattedValue)
        }
      })
    }
  }

  let {
    legendType,
    binCount,
    legendRange,
    getColorScale,
    min,
    mid,
    max,
    binType,
    legendValuesForStd,
  } = calculateColorScale(
    scaleType,
    indicatorMetadata?.admin_metadata?.[site]?.visualization,
    values,
    mapData?.[0],
    selectedUnitMeasure
  )
  // Color scheme for sequential, diverging, categorical --end

  const getFeatureDisplayName = (properties) => {
    if (properties.WB_NAME) {
      return properties.WB_NAME
    }

    if (properties.WB_REGION) {
      const regionCodes = properties.WB_REGION.split(',')
      for (const code of regionCodes) {
        const name = indicatorSpecificRegions?.find(
          (item) => item?.code === code
        )?.description
        if (name) {
          return name
        }
      }
    }

    if (properties.CONTINENT) {
      return properties.CONTINENT
    }
    return ''
  }

  const getTooltipContent = (d) => {
    const displayName = getFeatureDisplayName(d.properties)

    if (
      d.properties.value === null ||
      d.properties.value === undefined ||
      (d.properties.value &&
        (d.properties.value === 'null' || d.properties.value === 'NaN'))
    ) {
      return `<div class="tooltip-top"> <span class="tooltip-country">${displayName} </span><span class="tooltip-year"> ${
        d.properties.year ? `, ${d.properties.year}` : ''
      }</span></div>
<div class="tooltip-bottom"> <span class="tooltip-value">Data not available</span></div>`
    } else {
      if (
        scaleType === ScaleTypes.Categorical ||
        selectedUnitMeasure === CATEGORICAL_UNITMEASURE
      ) {
        const value = isNumeric(d.properties.value)
          ? d.properties.value == 1
            ? 'Yes'
            : 'No'
          : d.properties.value
        return `<div class="tooltip-top"> <span class="tooltip-country">${displayName}, </span><span class="tooltip-year"> ${d.properties.year}</span></div>
  <div class="tooltip-bottom"> <span class="tooltip-value">${value}</span></div>`
      } else {
        const unitMult = formatUnitMult(
          disaggregationData,
          mapData?.[0]?.[disaggregations.UnitMult]
        )

        return `<div class="tooltip-top"> <span class="tooltip-country">${displayName}, </span><span class="tooltip-year"> ${
          d.properties.year
        }</span></div>
  <div class="tooltip-bottom"> <span class="tooltip-value">${formatNumberOnUnitMeasure(
    {
      value: d.properties.value,
      unitMeasure:
        selectedUnitMeasure || mapData?.[0]?.[disaggregations.UnitMeasure],
      decimals: mapData?.[0]?.[disaggregations.Decimals],
      isCommaSeparatedFormat: true,
    }
  )}${unitMult ? ` ${unitMult}` : ''}${formatUnitMeasure(
    selectedUnitMeasure || mapData?.[0]?.[disaggregations.UnitMeasure],
    unitMeasureName ||
      unitMeasureList.find(
        (item) => item.code === mapData?.[0]?.[disaggregations.UnitMeasure]
      )?.description,
    shortLabel ||
      unitMeasureList.find(
        (item) => item.code === mapData?.[0]?.[disaggregations.UnitMeasure]
      )?.short_label
  )}</span></div>`
      }
    }
  }
  const badges = getBadges(
    selectedFilters,
    disaggregationData,
    selectedComparison,
    unitMeasureName
  )
  const badgesDiv =
    badges &&
    badges.length > 0 &&
    badges.map((badge) => (
      <div key={badge} className="text tui_badge">
        <div>
          <p>{badge}</p>
        </div>
      </div>
    ))

  return (
    <WorldMap
      features={features}
      countryCentroids={countryCentroids}
      getColorScale={mapData ? getColorScale : undefined}
      getTooltipContent={getTooltipContent}
      selectedCountries={selectedCountries}
      indicatorCode={indicatorMetadata?.series_description?.idno}
      scaleType={scaleType}
      boundaryType={boundaryType}
      indicatorName={
        indicatorMetadata?.admin_metadata?.[site]?.visualization
          ?.display_name || indicatorMetadata?.series_description.name
      }
      subtitle={getSingleYearChartSubtitle(
        selectedCountries,
        disaggregationCountries,
        recentYearSelection
          ? `Most recent values ${getRecentYearRange(mapData)}`
          : selectedSingleYear
      )}
      badgesDiv={badgesDiv}
      legendRange={legendRange}
      min={min}
      mid={mid}
      max={max}
      legendType={legendType}
      binCount={binCount}
      unitMeasure={
        selectedUnitMeasure || mapData?.[0]?.[disaggregations.UnitMeasure]
      }
      decimals={mapData?.[0]?.[disaggregations.Decimals]}
      missingDataColor={missingDataColor}
      binType={binType}
      legendValuesForStd={legendValuesForStd}
    />
  )
}
export default WorldMapWrapper
